import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "@/utils/api";

export const fetchUsers = createAsyncThunk(
  "users/fetchUsers",
  async (pagination, { rejectWithValue }) => {
    try {
      const response = await api.get("/admin/users", {
        params: {
          page: pagination.current_page,
          pageSize: pagination.pageSize,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const fetchDistrictUsers = createAsyncThunk(
  "users/fetchDistrictUsers",
  async (pagination, { rejectWithValue }) => {
    try {
      const response = await api.get("/admin/district_officers", {
        params: {
          page: pagination.current_page,
          pageSize: pagination.pageSize,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const fetchDistricts = createAsyncThunk(
  "users/fetchDistricts",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get("/master/districtswithassign");
      return response.data.districts || [];
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const createUser = createAsyncThunk(
  "users/createUser",
  async (userData, { rejectWithValue }) => {
    try {
      const response = await api.post("admin/users", userData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const updateUserStatus = createAsyncThunk(
  "users/updateUserStatus",
  async (statusData, { rejectWithValue }) => {
    try {
      const response = await api.post(
        `/admin/users/${statusData.user_id}/status`,
        {
          is_active: statusData.is_active,
          district_assignments: statusData.district_assignments,
          new_districts: statusData.new_districts,
        }
      );
      return response?.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const exportUsers = createAsyncThunk(
  "users/exportUsers",
  async (role_type, { rejectWithValue }) => {
    try {
      const response = await api.post("/admin/users/export", {
        role_type,
      });

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Export failed");
    }
  }
);

const initialState = {
  users: [],
  district_users: [],
  districts: [],
  pagination: {
    current_page: 1,
    pageSize: 10,
    totalPages: 1,
    total: 0,
  },
  loading: false,
  statusUpdating: false, 
  error: null,
  success: false,
};

const usersSlice = createSlice({
  name: "users",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSuccess: (state) => {
      state.success = false;
    },

    setPagination: (state, action) => {
      state.pagination = {
        ...state.pagination,
        ...action.payload,
      };
    },

    resetUsersState: () => initialState,
  },

  extraReducers: (builder) => {
    builder
      .addCase(updateUserStatus.pending, (state) => {
        state.statusUpdating = true;
        state.error = null;
      })
      .addCase(updateUserStatus.fulfilled, (state, action) => {
        state.statusUpdating = false;
        state.success = "User status updated successfully";
        const updateUser = (userList) => {
          return userList.map((user) =>
            user.id === action.payload.data.id ? action.payload.data : user
          );
        };
        state.users = updateUser(state.users);
        state.district_users = updateUser(state.district_users);
      })
      .addCase(updateUserStatus.rejected, (state, action) => {
        state.statusUpdating = false;
        state.error = action.payload || {
          message: "Failed to update user status",
        };
      });
    builder
      .addCase(fetchUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.users = action.payload.data;
        const p = action.payload.pagination;

        state.pagination = {
          current_page: p.current_page,
          pageSize: p.per_page,
          total: p.total,
          totalPages: p.totalPages,
        };
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch users";
      });
    builder
      .addCase(fetchDistricts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDistricts.fulfilled, (state, action) => {
        state.loading = false;
        state.districts = action.payload;
      })
      .addCase(fetchDistricts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch districts";
      });
    builder
      .addCase(fetchDistrictUsers.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchDistrictUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.district_users = action.payload.data;
        const p = action.payload.pagination;

        state.pagination = {
          current_page: p.current_page,
          pageSize: p.per_page,
          total: p.total,
          totalPages: p.totalPages,
        };
      })
      .addCase(fetchDistrictUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
    builder
      .addCase(createUser.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(createUser.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.users.push(action.payload);
      })
      .addCase(createUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to create user";
        state.success = false;
      });
  },
});

export const { clearError, clearSuccess, setPagination, resetUsersState } =
  usersSlice.actions;

export default usersSlice.reducer;
